import logger from './logger';

/**
 * Helper function to scroll sidebar to module
 * @param moduleId - The ID of the module or part of the link to scroll to
 */
export const scrollSidebarToModule = (moduleId: string) => {
    // Wait a bit to ensure the page/sidebar has rendered
    setTimeout(() => {
        // Find the module or sub-module link in the sidebar
        let moduleElement = document.querySelector(`div[main-item="${moduleId}"]`) as HTMLElement;

        // If not found as main item, try to find it as a link (for sub-modules)
        if (!moduleElement) {
            const allLinks = document.querySelectorAll('#navbar-nav a[href]');
            
            // Priority 1: Exact href match
            for (let i = 0; i < allLinks.length; i++) {
                const link = allLinks[i] as HTMLElement;
                const href = link.getAttribute('href');
                if (href === moduleId) {
                    moduleElement = link;
                    break;
                }
            }

            // Priority 2: Href ends with moduleId (handle relative/absolute differences)
            if (!moduleElement) {
                for (let i = 0; i < allLinks.length; i++) {
                    const link = allLinks[i] as HTMLElement;
                    const href = link.getAttribute('href');
                    if (href && href.endsWith(moduleId)) {
                        moduleElement = link;
                        break;
                    }
                }
            }

            // Priority 3: Href includes moduleId (fallback)
            if (!moduleElement) {
                for (let i = 0; i < allLinks.length; i++) {
                    const link = allLinks[i] as HTMLElement;
                    const href = link.getAttribute('href');
                    if (href && (href.includes(moduleId) || link.textContent?.toLowerCase().includes(moduleId.toLowerCase()))) {
                        moduleElement = link;
                        // Don't break here, to keep original behavior of finding *some* match if multiple exist? 
                        // Actually, breaking on first match is usually better than last match.
                        break; 
                    }
                }
            }
        }

        if (!moduleElement) {
            logger.debug('Module element not found for:', moduleId);
            return;
        }

        // Find the scrollable container - SimpleBar's content wrapper
        const scrollbar = document.getElementById('scrollbar');
        if (!scrollbar) {
            logger.debug('Scrollbar container not found');
            return;
        }

        // Try multiple approaches to get the scrollable element
        let scrollElement: HTMLElement | null = null;

        // Approach 1: SimpleBar instance
        const simpleBarInstance = (scrollbar as any).SimpleBar;
        if (simpleBarInstance && simpleBarInstance.getScrollElement) {
            scrollElement = simpleBarInstance.getScrollElement();
        }

        // Approach 2: Find content wrapper
        if (!scrollElement) {
            scrollElement = scrollbar.querySelector('.simplebar-content-wrapper') as HTMLElement;
        }

        // Approach 3: Check if scrollbar itself is scrollable
        if (!scrollElement && scrollbar.scrollHeight > scrollbar.clientHeight) {
            scrollElement = scrollbar;
        }

        if (!scrollElement) {
            // Last resort: use native scrollIntoView
            moduleElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
            return;
        }

        // Calculate scroll position to center the module
        const moduleRect = moduleElement.getBoundingClientRect();
        const containerRect = scrollElement.getBoundingClientRect();
        const currentScrollTop = scrollElement.scrollTop;

        // Calculate target scroll position (center the module)
        const moduleCenter = moduleRect.top + (moduleRect.height / 2);
        const containerCenter = containerRect.top + (containerRect.height / 2);
        const targetScrollTop = currentScrollTop + (moduleCenter - containerCenter);

        // Perform smooth scroll
        scrollElement.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
        });
    }, 200); // Increased delay to ensure DOM is ready
};
